// Remove: import { getSupabaseClient } from '../lib/supabase';

class Logger {
  // Remove: private supabase = getSupabaseClient();
  private static instance: Logger;
  private retryCount = 3;
  private retryDelay = 1000; // 1 second
  private tableChecked = false;

  private constructor() {
    this.checkLogsTable();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private async checkLogsTable(): Promise<void> {
    if (this.tableChecked) return;

    try {
      // Remove: const { error } = await this.supabase
      //   .from('logs')
      //   .select('id')
      //   .limit(1);

      if (/* error && error.code === '42P01' */ false) { // Table doesn't exist
        console.warn('Logs table does not exist. Creating it now...');
          await this.createLogsTable();
      }
    } catch (error) {
      console.error('Failed to check logs table:', error);
    } finally {
      this.tableChecked = true;
    }
  }

  private async createLogsTable(): Promise<void> {
    try {
      // Remove: const { error } = await this.supabase.rpc('create_logs_table');
      if (/* error */ false) {
        console.error('Failed to create logs table:', /* error */ null);
      }
    } catch (error) {
      console.error('Failed to create logs table:', error);
    }
  }

  private async sendToSupabase(level: string, message: string, category?: string, data?: Record<string, unknown>) {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    const logEntry = {
      level,
      message,
      category: category || 'general',
      data,
      timestamp: new Date().toISOString(),
      user_id: null,
      session_id: this.getSessionId()
    };

    let attempts = 0;
    while (attempts < this.retryCount) {
      try {
        // In production, we would send the log entry to Supabase
        console.log('Log entry:', logEntry);
        return;
      } catch (error) {
        attempts++;
        if (attempts === this.retryCount) {
          console.error('Failed to send log to Supabase after retries:', error);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * attempts));
      }
    }
  }

  private getSessionId(): string {
    return sessionStorage.getItem('sessionId') || crypto.randomUUID();
    }

  public async debug(message: string, category?: string, data?: Record<string, unknown>) {
    console.debug(`[${category || 'DEBUG'}] ${message}`, data);
    await this.sendToSupabase('debug', message, category, data);
  }

  public async info(message: string, category?: string, data?: Record<string, unknown>) {
    console.info(`[${category || 'INFO'}] ${message}`, data);
    await this.sendToSupabase('info', message, category, data);
  }

  public async warn(message: string, category?: string, data?: Record<string, unknown>) {
    console.warn(`[${category || 'WARN'}] ${message}`, data);
    await this.sendToSupabase('warn', message, category, data);
  }

  public async error(message: string, category?: string, data?: Record<string, unknown>) {
    console.error(`[${category || 'ERROR'}] ${message}`, data);
    await this.sendToSupabase('error', message, category, data);
  }

  public async logUserAction(action: string, data?: Record<string, unknown>) {
    await this.info(`User action: ${action}`, 'user_action', data);
  }

  public async logApiCall(endpoint: string, method: string, status: number, data?: Record<string, unknown>) {
    await this.info(
      `API call: ${method} ${endpoint} - ${status}`,
      'api_call',
      { endpoint, method, status, ...data }
    );
  }

  public async logPerformance(metric: string, value: number, unit: string = "ms", properties?: Record<string, unknown>) {
    const performanceData = {
      metric,
      value,
      unit,
      timestamp: new Date().toISOString(),
      ...properties
    };
    await this.info(
      `Performance: ${metric} = ${value}${unit}`,
      'performance',
      performanceData
    );
  }

  public async logWebVital(metric: { name: string; value: number; rating?: string; navigationType?: string }) {
    await this.logPerformance(metric.name, metric.value, 'ms', {
      rating: metric.rating,
      navigationType: metric.navigationType
    });
  }

  // Clear stored logs
  clearLogs(): void {
    localStorage.removeItem("rightNowAid_errorLogs");
  }
}

export const logger = Logger.getInstance();

// Performance monitoring utilities
export const measurePerformance = async <T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> => {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - start;
    logger.logPerformance(name, duration);
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    logger.error(`Performance measurement failed for ${name}`, error instanceof Error ? error.message : String(error));
    logger.logPerformance(`${name}_failed`, duration);
    throw error;
  }
};

// API call wrapper with logging
export const loggedApiCall = async <T>(
  endpoint: string,
  method: string,
  apiCall: () => Promise<T>
): Promise<T> => {
  const start = performance.now();
  try {
    const result = await apiCall();
    const duration = performance.now() - start;
    logger.logApiCall(endpoint, method, 200, {
      endpoint,
      method,
      duration,
      success: true,
    });
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    logger.logApiCall(endpoint, method, 500, {
      endpoint,
      method,
      duration,
      success: false,
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } : error,
    });
    throw error;
  }
};
